import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { useOrganization } from "@/hooks/useOrganization";
import { orgScopedKey } from "@/lib/queryKeys";
import { fetchAllRows } from "@/lib/fetchAllRows";
import {
  isNotNeeded,
  needsDocument,
  type IncomingRow,
  type MissingDocTransaction,
} from "@/lib/missingDocuments/rules";

const COLUMNS =
  "id, date, amount, currency, counter_name, counter_account, counter_bank_code, description, category, status, matched, receipt_id, invoice_id";

export interface MissingDocumentsData {
  /** Outgoing payments that still owe a document, newest first. */
  missing: MissingDocTransaction[];
  /** Outgoing payments marked "Nepotřebné", newest first. */
  notNeeded: MissingDocTransaction[];
  /** Incoming payments in the same window (own-transfer suggestions). */
  incoming: IncomingRow[];
  /**
   * Whether the company has any payment at all (any date). False before Fio is
   * connected or a statement uploaded, so the page can say so instead of "all done".
   */
  hasAnyTransactions: boolean;
}

/**
 * The page's rows for the current company since `from` (YYYY-MM-DD, null = all).
 * Keyed under "transactions", so the existing match / unmatch / status
 * invalidations refresh it.
 */
export function useMissingDocuments(from: string | null) {
  const { data: user } = useCurrentUser();
  const { currentOrganization } = useOrganization();
  const orgId = currentOrganization?.id;

  return useQuery({
    queryKey: orgScopedKey("transactions", orgId ?? "", "missing-documents", from ?? "all"),
    enabled: !!user && !!orgId,
    queryFn: async ({ signal }): Promise<MissingDocumentsData> => {
      const outgoing = await fetchAllRows<MissingDocTransaction>(
        () => {
          let q = supabase
            .from("transactions")
            .select(COLUMNS)
            .eq("organization_id", orgId!)
            .lt("amount", 0)
            // matched false/null, or marked not needed (which is matched = true)
            .or("matched.is.null,matched.eq.false,status.eq.not_needed");
          if (from) q = q.gte("date", from);
          return q as never;
        },
        { order: [{ column: "date", ascending: false, nullsFirst: false }], signal }
      );
      const incoming = await fetchAllRows<IncomingRow>(
        () => {
          let q = supabase
            .from("transactions")
            .select("id, date, amount, currency")
            .eq("organization_id", orgId!)
            .gt("amount", 0);
          if (from) q = q.gte("date", from);
          return q as never;
        },
        { order: [{ column: "date", ascending: false, nullsFirst: false }], signal }
      );
      // Only asked when the window is empty: one head-only count, no rows.
      let hasAnyTransactions = outgoing.length > 0 || incoming.length > 0;
      if (!hasAnyTransactions) {
        const { count, error } = await supabase
          .from("transactions")
          .select("id", { count: "exact", head: true })
          .eq("organization_id", orgId!);
        if (error) throw error;
        hasAnyTransactions = (count ?? 0) > 0;
      }
      return {
        missing: outgoing.filter(needsDocument),
        notNeeded: outgoing.filter(isNotNeeded),
        incoming,
        hasAnyTransactions,
      };
    },
  });
}

/**
 * How many outgoing payments of the last 90 days still owe a document: the
 * sidebar badge. Head-only count, the same rule as the page.
 */
export function useMissingDocumentsCount(from: string, options?: { enabled?: boolean }) {
  const { data: user } = useCurrentUser();
  const { currentOrganization } = useOrganization();
  const orgId = currentOrganization?.id;

  return useQuery({
    queryKey: orgScopedKey("transactions", orgId ?? "", "missing-documents-count", from),
    enabled: options?.enabled !== false && !!user && !!orgId,
    staleTime: 30 * 1000,
    queryFn: async () => {
      const { count, error } = await supabase
        .from("transactions")
        .select("id", { count: "exact", head: true })
        .eq("organization_id", orgId!)
        .lt("amount", 0)
        .not("matched", "is", true)
        .or("status.is.null,status.eq.missing")
        .gte("date", from);
      if (error) throw error;
      return count ?? 0;
    },
  });
}
